import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, Checkbox, DatePicker, Input, MoneyInput, PageHeader, Select, Spinner, Textarea, useToast } from '../../../components/ui';
import { StaffPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useDebounce } from '../../../hooks/useDebounce';
import { labelOf } from '../../../utils/labels';
import { PAY_TYPES, PAYMENT_MODE_OPTIONS } from '../../../constants/payments';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { formatDate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { rupeesInWords } from '../../../lib/pricing';
import { teamService } from '../../team/services/teamService';
import { payrollService } from '../services/payrollService';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';
import type { PayslipPreview } from '../types/payrollTypes';
import type { User } from '../../team/types/teamTypes';

// Quick periods: daily-wage workers are often paid weekly, monthly staff by calendar month.
/** Quick periods: [key, label, from, to] */
function periods(): [key: string, label: string, from: string, to: string][] {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const monday = new Date(y, m, now.getDate() - ((now.getDay() + 6) % 7));
  const iso = toISODate;
  return [
    ['month', 'This month', iso(new Date(y, m, 1)), iso(now)],
    ['lastMonth', 'Last month', iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    ['week', 'This week', iso(monday), iso(now)],
    ['lastWeek', 'Last week', iso(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7)), iso(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 1))],
  ];
}

/** Make a salary slip: pay is worked out from attendance; add bonus, deductions and advance recovery. */
export default function PayslipForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const P = periods();
  const [staff, setStaff] = useState<User | null>(null);
  const [preview, setPreview] = useState<PayslipPreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [advanceTouched, setAdvanceTouched] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm({
    userId: params.get('userId') || null, from: P[0][2] as string, to: P[0][3] as string, bonus: null, otherDeductions: null, advanceDeduction: null, note: '',
    payNow: false, payAmount: null, payMode: 'cash', payRef: '', payDate: toISODate(new Date()),
  });
  const key = useDebounce(JSON.stringify([form.userId, form.from, form.to, form.bonus, form.otherDeductions, form.advanceDeduction]), 300);

  useEffect(() => {
    if (form.userId && !staff) teamService.get(form.userId).then(setStaff).catch(() => {});
  }, [form.userId, staff]);

  // Live calculation from the server (same maths the slip will use).
  useEffect(() => {
    if (!form.userId || !form.from || !form.to) {
      setPreview(null);
      return undefined;
    }
    let alive = true;
    setCalculating(true);
    payrollService
      .preview({
        userId: form.userId as string, from: form.from, to: form.to,
        bonus: form.bonus ?? 0, otherDeductions: form.otherDeductions ?? 0, advanceDeduction: form.advanceDeduction ?? 0,
      })
      .then((p) => {
        if (!alive) return;
        setPreview(p);
        setPreviewError('');
        // First time for this person: recover as much pending advance as the pay allows.
        if (!advanceTouched && p.advanceBalance > 0 && form.advanceDeduction == null) {
          const room = p.grossAmount - p.otherDeductions;
          setForm((f) => ({ ...f, advanceDeduction: Math.max(0, Math.min(p.advanceBalance, room)) }));
        }
      })
      .catch((err) => alive && (setPreview(null), setPreviewError(err.message)))
      .finally(() => alive && setCalculating(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (preview && form.payNow) setForm((f) => ({ ...f, payAmount: preview.netAmount }));
  }, [preview?.netAmount, form.payNow, setForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickStaff = (id: string | null, u: User | null) => {
    setStaff(u);
    setAdvanceTouched(false);
    // Monthly staff default to last full month, daily-wage workers to this week.
    const p = u?.payType === 'monthly' ? P[1] : u?.payType === 'daily' ? P[2] : P[0];
    setForm((f) => ({ ...f, userId: id, from: p[2], to: p[3], advanceDeduction: null }));
  };
  const activePeriod = P.find(([, , f, t]) => f === form.from && t === form.to)?.[0];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.userId) errs.userId = 'Pick the staff member';
    if (form.payNow && !(form.payAmount > 0)) errs.payAmount = 'Enter the amount paid';
    if (form.payNow && preview && form.payAmount > preview.netAmount) errs.payAmount = 'More than the net pay';
    setErrors(errs);
    if (Object.keys(errs).length || previewError) return toast.error(previewError || 'Please fix the highlighted fields');
    if (preview?.overlap) return toast.error(`Slip ${preview.overlap.slipNo} already covers part of this period`);
    setSaving(true);
    try {
      const data = await payrollService.create({
        userId: form.userId as string, from: form.from, to: form.to,
        bonus: form.bonus ?? 0, otherDeductions: form.otherDeductions ?? 0, advanceDeduction: form.advanceDeduction ?? 0, note: trimOrUndef(form.note),
        ...(form.payNow ? { payment: { amount: form.payAmount, mode: form.payMode, referenceNo: trimOrUndef(form.payRef), date: form.payDate } } : {}),
      });
      toast.success(`Salary slip ${data.slipNo} saved`);
      navigate(`/payroll/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  const p = preview;
  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate('/payroll')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="payslip-form" leftIcon="check" loading={saving} disabled={!p || !!previewError}>Save salary slip</Button>
    </>
  );

  return (
    <>
      <PageHeader title="Make salary slip" subtitle="Pay is worked out from attendance. Daily wage: days worked × wage. Monthly: salary minus absent days." actions={actions} />
      <form id="payslip-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Who and which period">
                <StaffPicker label="Staff member" required value={form.userId} selectedLabel={staff?.name} onChange={pickStaff} error={errors.userId} containerClassName="sm:col-span-2" />
                <div className="flex flex-wrap gap-1.5 sm:col-span-2">
                  {P.map(([k, label, from, to]) => (
                    <Button key={k} size="sm" variant={activePeriod === k ? 'primary' : 'outline'} onClick={() => setForm((f) => ({ ...f, from, to }))}>
                      {label}
                    </Button>
                  ))}
                </div>
                <DatePicker label="From" value={form.from} onChange={set('from')} max={form.to || undefined} clearable={false} />
                <DatePicker label="To" value={form.to} onChange={set('to')} min={form.from || undefined} clearable={false} />
                {previewError && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2">
                    {previewError} {/Team/.test(previewError) && form.userId && <Link to={`/team/${form.userId}/edit`} className="font-medium underline">Open Team</Link>}
                  </p>
                )}
                {p?.overlap && (
                  <p className="rounded-lg border border-sun-200 bg-sun-50 px-3 py-2 text-sm text-sun-700 sm:col-span-2">
                    Salary slip <Link to={`/payroll/${p.overlap._id}`} className="font-medium underline">{p.overlap.slipNo}</Link> already covers part of this period.
                  </p>
                )}
              </FormSection>

              <FormSection title="Additions and deductions">
                <MoneyInput label="Bonus / incentive" value={form.bonus} onChange={set('bonus')} placeholder="0" />
                <MoneyInput label="Other deductions" value={form.otherDeductions} onChange={set('otherDeductions')} placeholder="0" hint="e.g. damage, fine" />
                <MoneyInput
                  label="Recover from advance"
                  value={form.advanceDeduction}
                  onChange={(v) => {
                    setAdvanceTouched(true);
                    set('advanceDeduction')(v);
                  }}
                  placeholder="0"
                  hint={p ? (p.advanceBalance > 0 ? `${money(p.advanceBalance)} advance pending` : 'No advance pending') : undefined}
                  disabled={!!p && p.advanceBalance <= 0}
                />
                <Textarea label="Note" rows={2} value={form.note} onChange={set('note')} maxLength={500} containerClassName="sm:col-span-2" />
              </FormSection>

              <FormSection title="Payment">
                <div className="sm:col-span-2">
                  <Checkbox label="Paid now" description="Records the salary payment with the slip. You can also pay later, in parts." checked={form.payNow} onChange={set('payNow')} />
                </div>
                {form.payNow && (
                  <>
                    <MoneyInput label="Amount paid" value={form.payAmount} onChange={set('payAmount')} error={errors.payAmount} />
                    <Select label="Mode" options={PAYMENT_MODE_OPTIONS} value={form.payMode} onChange={set('payMode')} searchable={false} />
                    <DatePicker label="Paid on" value={form.payDate} onChange={set('payDate')} max={toISODate(new Date())} clearable={false} />
                    <Input label="UTR / cheque no" value={form.payRef} onChange={set('payRef')} placeholder="Optional" />
                  </>
                )}
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <>
              <Card>
                <CardHeader title="Pay calculation" action={calculating && <Spinner size={16} className="text-ink-400" />} />
                <div className="p-5 pt-4">
                  {!p ? (
                    <p className="text-sm text-ink-400">{form.userId ? 'Working out…' : 'Pick a staff member to see the pay.'}</p>
                  ) : (
                    <>
                      <KeyValues
                        rows={[
                          ['Pay type', <Badge tone="blue">{labelOf(PAY_TYPES, p.payType)}</Badge>],
                          [p.payType === 'daily' ? 'Daily wage' : 'Monthly salary', money(p.rate)],
                          ['Days paid', `${p.payableDays} of ${p.days}`],
                          ['Basic pay', money(p.basicAmount)],
                          p.overtimeHours > 0 && [`Overtime ${p.overtimeHours} h × ${money(p.overtimeRate)}`, money(p.overtimeAmount)],
                          p.bonus > 0 && ['Bonus', money(p.bonus)],
                          ['Earnings', money(p.grossAmount), { strong: true }],
                          p.otherDeductions > 0 && ['Other deductions', `− ${money(p.otherDeductions)}`],
                          p.advanceDeduction > 0 && ['Advance recovered', `− ${money(p.advanceDeduction)}`],
                        ]}
                      />
                      <div className="mt-4 rounded-lg bg-brand-50 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-navy-900">Net pay</span>
                          <span className="tabular text-xl font-semibold text-brand-700">{money(p.netAmount)}</span>
                        </div>
                        <p className="mt-1 text-xs text-ink-500">{rupeesInWords(p.netAmount)}</p>
                      </div>
                    </>
                  )}
                </div>
              </Card>
              {p && (
                <Card>
                  <CardHeader title="Attendance" action={<Link to={`/attendance?view=sheet&month=${form.to.slice(0, 7)}`} className="text-sm font-medium text-brand-600 hover:underline">Open sheet</Link>} />
                  <div className="p-5 pt-4">
                    <KeyValues
                      rows={[
                        ['Period', `${formatDate(p.periodFrom)} – ${formatDate(p.periodTo)}`],
                        ['Present', p.presentDays],
                        ['Half day', p.halfDays],
                        ['Absent', p.absentDays],
                        ['Off', p.holidayDays],
                        ['Not marked', p.unmarkedDays, { tone: p.unmarkedDays && p.payType === 'daily' ? 'text-sun-600' : undefined }],
                      ]}
                    />
                    {(p.unmarkedDays ?? 0) > 0 && (
                      <p className="mt-3 text-xs text-ink-500">
                        {p.payType === 'daily' ? 'Days not marked are not paid for daily-wage workers.' : 'Days not marked are paid for monthly staff.'}
                      </p>
                    )}
                  </div>
                </Card>
              )}
            </>
          }
        />
      </form>
    </>
  );
}
